import {
  Anchor,
  Button,
  Div,
  Flex,
  Icon,
  Separator,
  Text,
} from '@sendoutcards/quantum-design-ui'
import moment from 'moment'
import React from 'react'
import { ProgressBar } from 'src/design_system/components/ProgressBar/ProgressBar'
import {
  PlanFragment,
  StripeSubscriptionWithItemsFragment,
} from 'src/graphql/generated/graphql'
import { useMemo, useSelector, useState } from 'src/hooks'
import { CurrentPlanCardItem } from './CurrentPlanCardItem'
import { ACT_ADDON_STRIPE_ID } from 'src/marketing/plans/constants'

type CurrentPlanCardProps = {
  title: string
  price: string
  description?: string
  fillHeight?: boolean
  currentSubscription?: StripeSubscriptionWithItemsFragment
  newPlan?: PlanFragment
  recurrence?: 'month' | 'year'
  changeAction?: {
    title: string
    onClick: () => void
    isDisabled?: boolean
  }
  renewAction?: {
    title: string
    onClick: () => void
    isDisabled?: boolean
  }
  cancelAction?: {
    title: string
    onClick: () => void
    isDisabled?: boolean
  }
  addAddonAction?: {
    title: string
    onClick: () => void
    isDisabled?: boolean
  }
  changeItemAction?: {
    title: string
    onClick: () => void
    isDisabled?: boolean
  }
  cancelItemAction?: {
    title: string
    onClick: (itemId: string) => void
    isDisabled?: boolean
  }
}

export const CurrentPlanCard: React.FC<CurrentPlanCardProps> = ({
  title,
  description = 'The perfect way to get started',
  price,
  fillHeight = false,
  currentSubscription,
  changeAction,
  renewAction,
  cancelAction,
  addAddonAction,
  changeItemAction,
  cancelItemAction,
  recurrence = 'month',
  newPlan,
}) => {
  const renewalDate = currentSubscription?.currentPeriodEnd
  const { isMobile, width } = useSelector(state => state.window)
  const isSmallMobile = width <= 418

  const cancelDate = currentSubscription?.isSetToCancel
    ? currentSubscription.currentPeriodEnd
    : undefined

  const getSubscriptionProgress = useMemo(() => {
    if (!renewalDate) return
    const today = new Date()
    const renewalDateTime = new Date(renewalDate)
    const totalDays = 30
    const progressDate = cancelDate ? new Date(cancelDate) : renewalDateTime

    const remainingDays = Math.round(
      (progressDate.getTime() - today.getTime()) / (1000 * 3600 * 24),
    )
    const percentage = ((totalDays - remainingDays) / totalDays) * 100
    return {
      remainingDays,
      percentage,
      progressDate,
    }
  }, [cancelDate, renewalDate])
  const [isManaging, setIsManaging] = useState(false)

  const hasUnlimitedActs =
    currentSubscription?.items.findIndex(
      item => item.sku === ACT_ADDON_STRIPE_ID,
    ) !== -1
  const showAddUnlimitedActs = addAddonAction && !hasUnlimitedActs

  const hasMultipleItems =
    currentSubscription && currentSubscription.items.length > 1

  return (
    <Flex
      flexDirection="column"
      backgroundColor={'foreground'}
      borderRadius="32px"
      inset={{ vertical: 'x2', horizontal: 'x2_5' }}
      boxShadow="light"
      style={fillHeight ? { height: '100%', flex: 1, minHeight: 0 } : undefined}
    >
      <Flex
        id="planInfoContainer"
        flexDirection="column"
        style={fillHeight ? { flex: 1, minHeight: 0 } : undefined}
      >
        <Flex
          justifyContent="space-between"
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems="center"
          outset={{ bottom: 'x3' }}
        >
          <Div width="100%">
            <Text
              type="body"
              color="primaryHeading"
              alignment="left"
              weight="bold"
              content={title}
            />
            <Text type="caption" content={description} />
          </Div>
          <Flex
            width="100%"
            alignItems="flex-end"
            justifyContent="flex-end"
            outset={isMobile ? { top: 'x2' } : undefined}
          >
            <Text type="subtitle" weight="bold" content={`${price}`} />
            <Text type="caption" weight="semiBold" content={`/${recurrence}`} />
          </Flex>
        </Flex>
        {hasMultipleItems && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.25rem',
              margin: '0.5rem 0',
            }}
          >
            {currentSubscription.items.map(item => (
              <CurrentPlanCardItem
                key={item.id}
                item={item}
                recurrence={recurrence}
                isManaging={isManaging}
                changeAction={changeItemAction}
                cancelAction={
                  cancelItemAction && {
                    ...cancelItemAction,
                    onClick: () => cancelItemAction.onClick(item.id),
                  }
                }
              />
            ))}
          </div>
        )}
        <Flex flexDirection={'column'} outset={{ bottom: 'x3' }}>
          {currentSubscription && (
            <Flex justifyContent="space-between" outset={{ bottom: 'x1' }}>
              <Text
                type="footnote"
                content={`${getSubscriptionProgress?.remainingDays} Days`}
                weight="semiBold"
                color="primaryHeading"
              />
              <Text
                type="footnote"
                color={cancelDate && !newPlan ? 'danger' : 'primaryBody'}
                content={`${getSubscriptionProgress?.remainingDays} ${
                  cancelDate && !newPlan
                    ? 'days until account is converted.'
                    : cancelDate && newPlan
                      ? 'days until account is converted.'
                      : 'days until renewal'
                }`}
              />
            </Flex>
          )}
          {getSubscriptionProgress && (
            <ProgressBar
              percentage={getSubscriptionProgress.percentage ?? ''}
              fillColor="#ffb854"
            />
          )}
          {cancelDate && (
            <Flex outset={{ top: 'x1' }} flexDirection="column">
              <Text
                type="footnote"
                color={cancelDate && !newPlan ? 'danger' : 'success'}
                weight="semiBold"
                content={`Will ${
                  cancelDate && !newPlan ? 'Cancel' : 'Change'
                } on: ${moment(currentSubscription?.currentPeriodEnd).format(
                  'MM/DD/YYYY',
                )}`}
              />
              {newPlan && (
                <Flex alignItems="center" outset={{ top: 'x_5' }}>
                  <Icon
                    name="swap"
                    size={'xSmall'}
                    primaryColor="primaryHeadingText"
                  />
                  <Text
                    type="footnote"
                    content={'Switching to: '}
                    color="primaryBody"
                    outset={{ horizontal: 'x_5' }}
                  />
                  <Text
                    type="footnote"
                    weight="bold"
                    content={`${newPlan?.title ?? 'Basic'}`}
                    style={{ color: '#be9cdd' }}
                  />
                </Flex>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
      <Separator orientation="horizontal" />
      <Flex
        id="ctaContainer"
        width="100%"
        justifyContent={'space-between'}
        alignItems="center"
        outset={{ top: 'x2' }}
        flexDirection={isMobile ? 'column' : 'row'}
      >
        {cancelAction && (
          <Flex
            justifyContent="flex-start"
            alignItems="center"
            outset={isMobile ? { bottom: 'x1' } : undefined}
            width="100%"
          >
            <Anchor
              title={cancelAction.title}
              size="xSmall"
              onClick={cancelAction.onClick}
            />
          </Flex>
        )}
        <Flex
          columnGap="x2"
          alignItems={isMobile ? 'flex-end' : undefined}
          rowGap="x2"
          style={{ marginLeft: 'auto' }}
          flexDirection={isSmallMobile ? 'column-reverse' : 'row'}
        >
          {renewAction && (
            <Button
              title={renewAction.title}
              size="small"
              type="smoke"
              onClick={renewAction.onClick}
              disabled={renewAction.isDisabled}
            />
          )}
          {addAddonAction && showAddUnlimitedActs && (
            <Button
              title={addAddonAction.title}
              size="xSmall"
              fontWeight="bold"
              onClick={() => addAddonAction.onClick()}
              disabled={addAddonAction.isDisabled}
              backgroundColorOverride="#424548"
            />
          )}
          {hasMultipleItems ? (
            isManaging ? (
              <Button
                title="Dismiss"
                size="small"
                type="smoke"
                onClick={() => setIsManaging(false)}
              />
            ) : (
              <Button
                title="Manage"
                size="small"
                type="smoke"
                onClick={() => setIsManaging(true)}
              />
            )
          ) : (
            changeAction && (
              <Button
                title={changeAction.title}
                size="xSmall"
                backgroundColorOverride="#EEF0F2"
                textColorOverride="#424548"
                onClick={changeAction.onClick}
                disabled={changeAction.isDisabled}
              />
            )
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}
